import React, { useState, ChangeEvent, KeyboardEvent } from "react";

import { numberToWord } from "../../utils/utils";

interface EmailChipsProps {
  emails: string[];
  setEmails: (value: any) => void;
  placeholder: string;
  label: string;
  disable?: boolean;
  keyName: string;
  error: { [key: string]: string };
  setError: (value: any) => void;
  mandatory?: boolean;
  limit?: number;
  setHasChanged?: (value: boolean) => void;
  noLimit?: boolean;
}

const EmailChips: React.FC<EmailChipsProps> = ({
  emails,
  setEmails,
  placeholder,
  label,
  disable,
  keyName,
  error,
  setError,
  mandatory,
  limit = 5,
  setHasChanged,
  noLimit = false,
}) => {
  const [email, setEmail] = useState<string>("");

  /**
   * Handles changes in the email input field.
   * @param e - The event object from the input change.
   */
  const handleChange = (e: ChangeEvent<HTMLInputElement>): void => {
    const { value } = e.target;
    setEmail(value);
    setError((prev: any) => ({
      ...prev,
      [keyName]: "",
    }));
  };

  /**
   * Validates the format of the given email address.
   * @param email - The email address to validate.
   * @returns True if the email format is valid, otherwise false.
   */
  const validateEmail = (email: string): boolean => {
    const re = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return re.test(String(email).toLowerCase());
  };

  const validateMultipleEmails = (emails: string[]): boolean => {
    // Loop through each email and validate
    return emails.every(email => validateEmail(email));
  };

  /**
   * Adds the current email to the list if it is valid and not already included.
   * Clears the email input field upon success.
   */
  const addEmail = () => {
    if (!noLimit && emails.length >= limit) {
      const limitWord = numberToWord(limit); // Convert the limit to its word form
      setError((prev: any) => ({
        ...prev,
        [keyName]: `Please enter only ${limitWord} email${
          limit > 1 ? "s" : ""
        }.`,
      }));
      return;
    }
    
    let emailIds = email.replace(/ /g, "").split(/[;,]+/);
    if (emailIds[emailIds.length - 1] === "") {
      emailIds.pop();
    }
    if (emailIds.length === 0) {
      setError((prev: any) => ({ ...prev, [keyName]: "" }));
      return;
    }
    if (validateMultipleEmails(emailIds)) {
      // if (!emails.includes(email)) {
      if ((!emails.some((x) => emailIds.some(
        (id: string) =>
          id.toLowerCase() === x.toLowerCase()
      )))) { 
        setHasChanged && setHasChanged(true);
        setEmails((prev: any) => ({
          ...prev,
          [keyName]: [...emails, ...emailIds],
        }));
        setEmail("");
        setError((prev: any) => ({
          ...prev,
          [keyName]: "",
        }));
      } else {
        setError((prev: any) => ({
          ...prev,
          [keyName]: "Email already exists",
        }));
      }
    } else if (email) {
      setError((prev: any) => ({
        ...prev,
        [keyName]: "Invalid email address",
      }));
    }
  };

  /**
   * Handles keydown events in the email input field.
   * Adds the email if the Enter, comma, or space keys are pressed.
   * @param e - The event object from the keydown event.
   */

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>): void => {
    if (e.key === "Enter" || e.key === "," || e.key === " " || e.key === ";") {
      e.preventDefault();
      addEmail();
    }
  };

  /**
   * Handles the blur event on the email input field.
   * Adds the email when the input field loses focus.
   * @param e - The event object from the blur event.
   */
  const handleBlur = (e: React.FocusEvent<HTMLInputElement>): void => {
    if (!noLimit && emails.length >= limit) {
      return;
    }
    addEmail();
    if (mandatory && emails.length === 0 && !email.trim()) {
      setError((prev: any) => ({
        ...prev,
        [keyName]: "Please enter an email",
      }));
    }
  };

  /**
   * Removes an email from the list based on the index.
   * Does nothing if the component is disabled.
   * @param index - The index of the email to remove.
   */
  const removeEmail = (index: number): void => {
    if (disable) {
      return;
    }
    setHasChanged && setHasChanged(true);
    setEmails((prev: any) => {
      const updated = prev[keyName].filter((_: string, i: number) => i !== index);
      if (mandatory && updated.length === 0) {
        setError((prevErr: any) => ({
          ...prevErr,
          [keyName]: "Please enter an email",
        }));
      }
      return { ...prev, [keyName]: updated };
    });
  };

  return (
    <>
      <label htmlFor="email" className="form__label">
        {label}
        {mandatory && <span className="mandatory-mark">*</span>}
      </label>
      <div className={`email-chips ${disable ? "email-chips--disabled" : ""}`}>
        <div className="chips-container">
          {emails?.map((email, index) => (
            <span key={`emailKeyIndex-${index}`} className="chip">
              <span className="chip__text">{email}</span>
              <button
                type="button"
                className="chip__close"
                onClick={() => removeEmail(index)}
              >
                &times;
              </button>
            </span>
          ))}
        </div>
        <input
          type="email"
          id="email"
          name="email"
          className="form__input form__input--sm"
          placeholder={placeholder}
          value={email}
          onChange={handleChange}
          onKeyDown={handleKeyDown}
          onBlur={handleBlur}
          disabled={disable}
        />
      </div>
      {error?.[keyName] && (
        <span className="form__error">{error?.[keyName]}</span>
      )}
    </>
  );
};

export default EmailChips;
